import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { IMG } from "@/lib/assets";
import { startVoice } from "@/lib/realtimeVoice";
import { PageBanner } from "@/components/ui/PageBanner";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { Flame, Snowflake, Clock, Play, Pause, SkipForward, X, Check, Mic, Square, ShieldAlert } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function WarmUp() {
  const [phase, setPhase] = useState("pre");
  const [active, setActive] = useState(null);
  const { data: routines = [] } = useQuery({ queryKey: ["warmups"], queryFn: async () => (await api.get("/api/warmups")).data });
  const list = routines.filter((r) => r.phase === phase);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.yoga} height="h-56 md:h-64" overline="prepare · recover" title="Warm-up & Stretch"
        subtitle="The part most athletes skip. Five to ten minutes before and after training cuts injury risk, improves your lifts and speeds up recovery." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          ["Before training", "Dynamic moves raise your temperature and heart rate and prime the joints you're about to load. No long static holds before lifting."],
          ["After training", "Slow stretches and easy breathing bring your heart rate down and restore range of movement while the muscles are warm."],
          ["Listen to your body", "Stretch to mild tension, never pain. Stop if you feel sharp pain, dizziness or numbness."],
        ].map(([t, b], i) => (
          <div key={t} className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-4">
            <p className="font-heading font-bold flex items-center gap-2">
              {i === 0 ? <Flame className="w-4 h-4 text-[#FFB340]" /> : i === 1 ? <Snowflake className="w-4 h-4 text-[#3DD6FF]" /> : <ShieldAlert className="w-4 h-4 text-acid" />}{t}
            </p>
            <p className="text-sm text-[#8A91A6] mt-1">{b}</p>
          </div>
        ))}
      </div>

      <div className="inline-flex rounded-full border border-[#343A4A] p-1">
        {[["pre", "Warm-ups", Flame], ["post", "Cool-downs & stretches", Snowflake]].map(([k, l, I]) => (
          <button key={k} onClick={() => setPhase(k)} data-testid={`warm-tab-${k}`}
            className={`px-4 h-9 rounded-full text-sm inline-flex items-center gap-1.5 ${phase === k ? "bg-acid text-black" : "text-[#8A91A6]"}`}><I className="w-4 h-4" />{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {list.map((r, i) => (
          <motion.div key={r.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -4 }}
            className="group rounded-xl overflow-hidden border border-[#1E222A] bg-[#0F1115] flex flex-col" data-testid="warm-card">
            <div className="relative h-40 overflow-hidden">
              <img src={IMG[r.image] || IMG.yoga} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(0deg,#0F1115 5%,rgba(15,17,21,0.15) 70%)" }} />
              <span className="absolute top-3 left-3 mono-label rounded-full px-2.5 py-1 bg-black/60 border border-white/10" style={{ color: r.phase === "pre" ? "#FFB340" : "#3DD6FF" }}>
                {r.phase === "pre" ? "warm-up" : "cool-down"}
              </span>
              <span className="absolute top-3 right-3 mono-label rounded-full px-2.5 py-1 bg-black/60 flex items-center gap-1"><Clock className="w-3 h-3" />{r.minutes} min</span>
              <h3 className="absolute bottom-2 left-4 right-4 font-heading font-black text-xl tracking-tight">{r.title}</h3>
            </div>
            <div className="p-4 flex flex-col flex-1">
              <p className="mono-label text-acid">{r.for}</p>
              <ol className="mt-2 space-y-1 text-sm text-[#B7BECB] flex-1">
                {r.steps.slice(0, 4).map((s, j) => <li key={j} className="truncate">{j + 1}. {s.name}{s.sides ? " (each side)" : ""}</li>)}
                {r.steps.length > 4 && <li className="text-[#565C6F]">+ {r.steps.length - 4} more</li>}
              </ol>
              <button onClick={() => setActive(r)} className="mt-4 h-11 rounded-full bg-acid text-black font-medium inline-flex items-center justify-center gap-2 hover:bg-[#AACC00]" data-testid="warm-start">
                <Play className="w-4 h-4" fill="currentColor" /> Start guided routine
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>{active && <Player routine={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </div>
  );
}

function stepSeconds(s) {
  const base = s.secs || (s.reps ? Math.max(20, s.reps * 4) : 30);
  return s.sides ? base * 2 : base;
}

function Player({ routine, onClose }) {
  const steps = routine.steps;
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(stepSeconds(steps[0]));
  const [running, setRunning] = useState(true);
  const [finished, setFinished] = useState(false);
  const [voice, setVoice] = useState("idle");
  const voiceRef = useRef(null);
  const step = steps[idx];
  const total = stepSeconds(step);

  useEffect(() => {
    if (!running || finished) return undefined;
    const t = setInterval(() => setLeft((l) => l - 1), 1000);
    return () => clearInterval(t);
  }, [running, finished]);

  useEffect(() => {
    if (left > 0) return;
    if (idx + 1 < steps.length) { setIdx(idx + 1); setLeft(stepSeconds(steps[idx + 1])); }
    else setFinished(true);
  }, [left]); // eslint-disable-line

  useEffect(() => () => voiceRef.current?.stop?.(), []);

  const skip = () => { if (idx + 1 < steps.length) { setIdx(idx + 1); setLeft(stepSeconds(steps[idx + 1])); } else setFinished(true); };
  const toggleVoice = async () => {
    if (voiceRef.current) { voiceRef.current.stop(); voiceRef.current = null; setVoice("idle"); return; }
    try { voiceRef.current = await startVoice({ topic: `warmup:${routine.id}`, onStatus: setVoice, onEvent: () => {} }); }
    catch (e) { setVoice("idle"); toast.error(e?.name === "NotAllowedError" ? "Please allow microphone access for voice guidance." : apiErr(e, "Voice guidance isn't available right now.")); }
  };
  const logIt = async () => {
    try {
      await api.post("/api/diary", { date: new Date().toISOString(), duration_min: routine.minutes, kind: "mobility",
        title: routine.title, rpe: routine.phase === "pre" ? 3 : 2, status: "done", notes: routine.phase === "pre" ? "Warm-up" : "Cool-down" });
      toast.success("Logged in your Training Diary");
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center p-4" data-testid="warm-player">
      <div className="relative w-full max-w-xl rounded-2xl overflow-hidden border border-[#1E222A] bg-[#0B0D11]">
        <img src={IMG[routine.image] || IMG.yoga} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 flex items-center justify-center" aria-label="Close"><X className="w-4 h-4" /></button>
        <div className="relative p-6 md:p-8">
          <p className="mono-label text-acid">{routine.title}</p>
          {!finished ? (
            <>
              <p className="mono-label mt-4">step {idx + 1} of {steps.length}</p>
              <AnimatePresence mode="wait">
                <motion.div key={idx} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                  <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-1">{step.name}</h2>
                  <p className="text-[#B7BECB] mt-2">{step.reps ? `${step.reps} reps${step.sides ? " each side" : ""}` : `${step.secs}s${step.sides ? " each side" : ""}`} · {step.cue}</p>
                </motion.div>
              </AnimatePresence>
              <div className="flex items-center justify-center my-8">
                <div className="relative w-44 h-44">
                  <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                    <circle cx="60" cy="60" r="52" stroke="#1E222A" strokeWidth="10" fill="none" />
                    <circle cx="60" cy="60" r="52" stroke={routine.phase === "pre" ? "#FFB340" : "#3DD6FF"} strokeWidth="10" fill="none" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 52} strokeDashoffset={2 * Math.PI * 52 * (left / total)} style={{ transition: "stroke-dashoffset 1s linear" }} />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center font-heading font-black text-4xl">{fmt(Math.max(left, 0))}</span>
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-[#1E222A] overflow-hidden"><div className="h-full bg-acid transition-all" style={{ width: `${(idx / steps.length) * 100}%` }} /></div>
              {idx + 1 < steps.length && <p className="text-sm text-[#8A91A6] mt-3">Next: {steps[idx + 1].name}</p>}
              <div className="flex flex-wrap gap-2 mt-5">
                <button onClick={() => setRunning(!running)} className="flex-1 h-11 rounded-full bg-acid text-black font-medium inline-flex items-center justify-center gap-2">
                  {running ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4" /> Resume</>}
                </button>
                <button onClick={skip} className="h-11 px-5 rounded-full border border-[#343A4A] inline-flex items-center gap-2"><SkipForward className="w-4 h-4" /> Next</button>
                <button onClick={toggleVoice} className={`h-11 px-5 rounded-full border inline-flex items-center gap-2 ${voice === "live" ? "border-acid text-acid" : "border-[#343A4A]"}`}>
                  {voice === "live" ? <><Square className="w-4 h-4" /> Voice on</> : <><Mic className="w-4 h-4" /> {voice === "connecting" ? "Connecting…" : "Coach voice"}</>}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <CoachAvatar size={80} />
              <h2 className="font-heading font-black text-3xl tracking-tight mt-4">{routine.phase === "pre" ? "You're warm. Go train." : "Nicely done. Recovery starts now."}</h2>
              <p className="text-[#B7BECB] mt-2">{routine.minutes} minutes that most people skip. Your body will thank you.</p>
              <div className="flex gap-2 mt-6">
                <button onClick={logIt} className="flex-1 h-11 rounded-full bg-acid text-black font-medium inline-flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Log it in my diary</button>
                <button onClick={onClose} className="flex-1 h-11 rounded-full border border-[#343A4A]">Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
